import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Container,
  Link as MuiLink,
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import passwordResetService from '../services/passwordReset';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Dirección de correo electrónico inválida')
        .required('El email es requerido'),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);
      
      try {
        await passwordResetService.forgotPassword(values.email);
        setSuccess(true);
      } catch (err: any) {
        setError(err.message || 'Error al solicitar restablecimiento de contraseña');
      } finally {
        setLoading(false);
      }
    },
  });

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            textAlign: 'center',
          }}
        >
          <EmailIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          
          <Typography variant="h4" component="h1" gutterBottom>
            Email Enviado
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Si el email existe en nuestro sistema, recibirás un enlace para restablecer tu contraseña.
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Revisa tu bandeja de entrada y la carpeta de spam. El enlace expirará en 2 horas.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/login')}
            >
              Volver al Login
            </Button>
            
            <Button
              variant="contained"
              onClick={() => {
                setSuccess(false);
                formik.resetForm();
              }}
            >
              Enviar Otro Email
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center">
          ¿Olvidaste tu Contraseña?
        </Typography>
        
        <Typography variant="body1" gutterBottom align="center" sx={{ mb: 4, color: 'text.secondary' }}>
          Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit}>
          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email"
            type="email"
            variant="outlined"
            margin="normal"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            InputProps={{
              startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Enviar Enlace de Restablecimiento'}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <MuiLink component={Link} to="/login" variant="body2">
              <ArrowBackIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
              Volver al Login
            </MuiLink>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default ForgotPassword;
